import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatPrice } from "@/lib/shopify";
import { Link } from "react-router-dom";

export default function CartDrawer() {
  const {
    cart,
    open,
    closeCart,
    updateItem,
    removeItem,
    loading,
  } = useCart();

  const lines = cart?.lines?.edges?.map((e) => e.node) || [];
  const subtotal = cart?.cost?.subtotalAmount;

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => (v ? null : closeCart())}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col bg-white p-0 sm:max-w-md"
        data-testid="cart-drawer"
      >
        <SheetHeader className="border-b border-blush bg-blush/60 px-6 py-5 text-left">
          <SheetTitle className="font-subheading text-2xl text-plum">
            Your Pink Bag
          </SheetTitle>
          <SheetDescription className="text-xs text-roseslate">
            {cart?.totalQuantity || 0} item
            {(cart?.totalQuantity || 0) === 1 ? "" : "s"} ready for checkout.
          </SheetDescription>
          <p className="sr-only">Cart drawer with product line items.</p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {lines.length === 0 ? (
            <div
              className="flex h-full flex-col items-center justify-center gap-4 text-center"
              data-testid="cart-empty"
            >
              <div className="grid h-20 w-20 place-items-center rounded-full bg-blush text-hotpink">
                <ShoppingBag className="h-8 w-8" />
              </div>
              <p className="font-subheading text-xl text-plum">
                Your bag is feeling un-pink.
              </p>
              <p className="text-sm text-roseslate">
                Add a little sparkle to your day.
              </p>
              <Link
                to="/shop"
                onClick={closeCart}
                className="btn-pink mt-2"
                data-testid="cart-empty-shop-link"
              >
                Start shopping
              </Link>
            </div>
          ) : (
            <ul className="space-y-5" data-testid="cart-lines">
              {lines.map((line) => {
                const m = line.merchandise;
                const opts = m.selectedOptions
                  ?.filter((o) => o.value !== "Default Title")
                  .map((o) => `${o.name}: ${o.value}`)
                  .join(" / ");
                return (
                  <li
                    key={line.id}
                    className="flex gap-4"
                    data-testid={`cart-line-${line.id}`}
                  >
                    {m.image?.url && (
                      <img
                        src={m.image.url}
                        alt={m.image.altText || m.product?.title}
                        className="h-24 w-20 rounded-xl object-cover"
                      />
                    )}
                    <div className="flex flex-1 flex-col justify-between">
                      <div>
                        <p className="font-subheading text-sm text-plum">
                          {m.product?.title}
                        </p>
                        {opts && (
                          <p className="text-xs text-roseslate">{opts}</p>
                        )}
                        <p className="mt-1 text-sm font-semibold text-magenta">
                          {formatPrice(m.price.amount, m.price.currencyCode)}
                        </p>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center gap-2 rounded-full border border-blush bg-rose px-2 py-1">
                          <button
                            type="button"
                            onClick={() =>
                              updateItem(line.id, Math.max(line.quantity - 1, 1))
                            }
                            disabled={loading}
                            className="grid h-6 w-6 place-items-center rounded-full text-hotpink hover:bg-white"
                            aria-label="Decrease quantity"
                            data-testid={`cart-decrease-${line.id}`}
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="min-w-6 text-center text-sm font-semibold">
                            {line.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => updateItem(line.id, line.quantity + 1)}
                            disabled={loading}
                            className="grid h-6 w-6 place-items-center rounded-full text-hotpink hover:bg-white"
                            aria-label="Increase quantity"
                            data-testid={`cart-increase-${line.id}`}
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(line.id)}
                          disabled={loading}
                          className="text-roseslate hover:text-magenta"
                          aria-label="Remove item"
                          data-testid={`cart-remove-${line.id}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {lines.length > 0 && (
          <div
            className="border-t border-blush bg-rose/60 px-6 py-5"
            data-testid="cart-footer"
          >
            <div className="flex items-center justify-between text-sm text-plum">
              <span>Subtotal</span>
              <span className="text-lg font-semibold text-magenta">
                {subtotal
                  ? formatPrice(subtotal.amount, subtotal.currencyCode)
                  : "$0.00"}
              </span>
            </div>
            <p className="mt-1 text-xs text-roseslate">
              Taxes and shipping calculated at checkout.
            </p>
            <a
              href={cart?.checkoutUrl}
              data-testid="cart-checkout-link"
              className="btn-pink mt-4 w-full justify-center text-base"
            >
              Checkout
            </a>
            <button
              type="button"
              onClick={closeCart}
              className="mt-3 w-full text-center text-sm font-medium text-roseslate hover:text-magenta"
              data-testid="cart-continue-shopping"
            >
              Continue shopping
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
